import styled from '@emotion/styled';
import { IconButton, Menu, MenuItem, Typography } from '@material-ui/core';
import MoreVert from '@material-ui/icons/MoreVert';
import { createElement, FC, memo, MouseEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { CreativeFormatCreativeType } from '../../../../generated/graphql';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import { InputtedCreativeFormat } from '../../../types/InputtedCreativeFormat';
import _CreativeFormatCreativeTypeIcon from '../CreativeFormatCreativeTypeIcon';
import _CreativeFormatDevice from '../CreativeFormatDevice';
import CreativeFormatNetworkIcon from '../CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from '../CreativeFormatPlacementLabel';
import EditAspectRatioDialog from './EditAspectRatioDialog';

type Props = {
  creativeFormat: InputtedCreativeFormat;
  onClickDeleteButton: ({ creativeFormatId }: { creativeFormatId: string }) => void;
  onSubmitEditingAspectRatio: ({
    creativeFormatId,
    width,
    height,
  }: {
    creativeFormatId: string;
    width: number;
    height: number;
  }) => void;
  className?: string;
};

const CreativeFormatOriginalItem: FC<Props> = ({
  creativeFormat,
  onClickDeleteButton,
  onSubmitEditingAspectRatio,
  className,
}) => {
  // AspectRatio編集モーダルのフラグ
  const [modalOpen, setModalOpen] = useState(false);

  const isAspectRatioVisible =
    creativeFormat.attribute.aspectRatio &&
    creativeFormat.attribute.aspectRatio.aspectRatio.width * creativeFormat.attribute.aspectRatio.aspectRatio.height >
      0;

  // サブメニューのフラグ
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // 各イベント
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickAspectRatio = () => {
    setAnchorEl(null);
    setModalOpen(true);
  };

  const handleClickDeleteItem = () => {
    setAnchorEl(null);
    onClickDeleteButton({
      creativeFormatId: creativeFormat.tmpid! || creativeFormat.id!,
    });
  };

  const handleSubmitAspectRatio = ({ width, height }: { width: number; height: number }) => {
    onSubmitEditingAspectRatio({
      creativeFormatId: creativeFormat.tmpid! || creativeFormat.id!,
      width,
      height,
    });
    setModalOpen(false);
  };

  return (
    <Root className={className}>
      <CreativeFormatCreativeTypeIcon creativeType={creativeFormat.creativeType as CreativeFormatCreativeType} />
      <Device>
        <CreativeFormatDevice
          network={creativeFormat.network}
          aspectRatio={creativeFormat.attribute.aspectRatio ? creativeFormat.attribute.aspectRatio.aspectRatio : null}
        />
      </Device>
      <FormatByNetworkAndPlacement>
        <NetworkIcon network={creativeFormat.network} size={24} />
        <PlacementName>
          <Typography variant="subtitle1">
            <CreativeFormatPlacementLabel placement={creativeFormat.placement} />
          </Typography>
        </PlacementName>
      </FormatByNetworkAndPlacement>
      <FormatByAttribute>
        {creativeFormat.attribute.aspectRatio && creativeFormat.attribute.aspectRatio.size ? (
          <Typography variant="body2">
            {creativeFormat.attribute.aspectRatio.size.width * creativeFormat.attribute.aspectRatio.size.height > 0 ? (
              `${creativeFormat.attribute.aspectRatio.aspectRatio.width}:
            ${creativeFormat.attribute.aspectRatio.aspectRatio.height}（${creativeFormat.attribute.aspectRatio.size.width}:
              ${creativeFormat.attribute.aspectRatio.size.height}）`
            ) : (
              <FormattedMessage id="creative_format.aspect_ratio.nullable" />
            )}
          </Typography>
        ) : null}
        {creativeFormat.attribute.aspectRatio && creativeFormat.attribute.playbackTime ? (
          <Typography variant="body2">/</Typography>
        ) : null}
        {creativeFormat.attribute.playbackTime ? (
          <Typography variant="body2">
            <FormattedMessage
              id="creative_format.playback_time"
              values={{
                seconds: creativeFormat.attribute.playbackTime.seconds,
              }}
            />
          </Typography>
        ) : null}
      </FormatByAttribute>
      <Actions>
        <IconButton aria-label="more" aria-controls="long-menu" aria-haspopup="true" onClick={handleClick}>
          <MoreVert />
        </IconButton>
        <Menu anchorEl={anchorEl} keepMounted open={menuOpen} onClose={handleClose}>
          {isAspectRatioVisible ? (
            <MenuItem onClick={handleClickAspectRatio}>
              <Typography variant="body2">
                <FormattedMessage id="creative_format_editor.creative_format_menu.aspect_ratio" />
              </Typography>
            </MenuItem>
          ) : null}
          <MenuItem onClick={handleClickDeleteItem}>
            <Typography variant="body2" color="error">
              <FormattedMessage id="creative_format_editor.creative_format_menu.delete_item" />
            </Typography>
          </MenuItem>
        </Menu>
      </Actions>
      {isAspectRatioVisible && creativeFormat.attribute.aspectRatio?.size ? (
        <EditAspectRatioDialog
          aspectRatio={creativeFormat.attribute.aspectRatio.aspectRatio}
          displaySize={creativeFormat.attribute.aspectRatio.size}
          isOpen={modalOpen}
          onSubmit={handleSubmitAspectRatio}
          onRequestClose={(_: any) => {
            setModalOpen(false);
          }}
        />
      ) : null}
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['auto'],
    rows: ['200px', 'auto', 'auto'],
    areas: [['device'], ['placement'], ['attribute']],
    alignItems: 'center',
  }),
  {
    position: 'relative',
    padding: SPACING_REGULAR,
    textAlign: 'center',
  },
);

const Device = styled('div')(gridLayoutItem('device', { justifySelf: 'center' }), {
  width: '100%',
  height: '100%',
  paddingBottom: SPACING_REGULAR,
});

const CreativeFormatDevice = styled(_CreativeFormatDevice)({
  width: '100%',
  height: '100%',
});

const CreativeFormatCreativeTypeIcon = styled(_CreativeFormatCreativeTypeIcon)({
  position: 'absolute',
  top: SPACING_REGULAR,
  left: SPACING_UNIT,
});

const FormatByNetworkAndPlacement = styled('div')(
  gridLayoutItem('placement', { justifySelf: 'center' }),
  flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'center',
  }),
  {
    marginBottom: SPACING_UNIT,
  },
);

const FormatByAttribute = styled('div')(
  gridLayoutItem('attribute', { justifySelf: 'center' }),
  flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'center',
  }),
);

const NetworkIcon = styled(CreativeFormatNetworkIcon)(gridLayoutItem('network', { justifySelf: 'center' }));

const PlacementName = styled('div')(gridLayoutItem('placement'), {
  width: '80%',
  wordBreak: 'break-all',
  marginLeft: SPACING_UNIT,
});

const Actions = styled('div')({
  position: 'absolute',
  top: SPACING_UNIT,
  right: 0,
});

export default memo(CreativeFormatOriginalItem);
